import { useAuth0 } from '@auth0/auth0-react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CloseIcon,
  HamburgerIcon,
} from '@chakra-ui/icons';
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Flex,
  Icon,
  IconButton,
  Image,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  AnimatePresence,
  useReducedMotion,
} from 'framer-motion';
import { MotionBox } from '../../motion/MotionComponents';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useNavigate } from 'react-router-dom';
import DenimLogo from '../../../assets/logo/denim-white-logo.svg';
import { usePermissions } from '../../../hooks/usePermissions';
import { LoginButton } from '../../../pages/auth/Login';
import { hasFollowUpSchedulingAccessFromStorage } from '../../../utils/permissionUtils';
import { clearAuthData } from '../../../utils/tokenValidation';
import OmniSearch from '../../omnisearch/OmniSearch';
import './nav-bar.css';

const ProfileMenu: React.FC<{
  user: { name?: string; picture?: string } | undefined;
  navigateToProfile: () => void;
  navigateToSettings: () => void;
  handleLogout: () => void;
}> = ({ user, navigateToProfile, navigateToSettings, handleLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const menuContent = createPortal(
    <AnimatePresence>
      {isOpen && (
        <MotionBox
          ref={menuRef}
          position="fixed"
          top={`${position.top}px`}
          right={`${position.right}px`}
          bg="white"
          boxShadow="lg"
          borderRadius="md"
          w="20rem"
          py={1}
          zIndex={100000}
          border="1px solid"
          borderColor="gray.200"
          overflow="hidden"
          initial={
            prefersReducedMotion
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: -6 }
          }
          animate={{ opacity: 1, y: 0 }}
          exit={
            prefersReducedMotion
              ? { opacity: 1, y: 0 }
              : { opacity: 0, y: -4 }
          }
        >
          <Button
            variant="ghost"
            w="100%"
            justifyContent="flex-start"
            color="blue.800"
            fontSize="sm"
            py={2}
            px={2}
            h="auto"
            fontWeight="normal"
            onClick={() => {
              navigateToProfile();
              setIsOpen(false);
            }}
            _hover={{ bg: 'blue.50' }}
            _active={{ bg: 'blue.100' }}
          >
            My profile ({user?.name})
          </Button>
          <Button
            variant="ghost"
            w="100%"
            justifyContent="flex-start"
            color="blue.800"
            fontSize="sm"
            py={2}
            px={2}
            h="auto"
            fontWeight="normal"
            onClick={() => {
              navigateToSettings();
              setIsOpen(false);
            }}
            _hover={{ bg: 'blue.50' }}
            _active={{ bg: 'blue.100' }}
          >
            Settings
          </Button>
          <Button
            variant="ghost"
            w="100%"
            justifyContent="flex-start"
            color="blue.800"
            fontSize="sm"
            py={2}
            px={2}
            h="auto"
            fontWeight="normal"
            onClick={() => {
              handleLogout();
              setIsOpen(false);
            }}
            _hover={{ bg: 'blue.50' }}
            _active={{ bg: 'blue.100' }}
          >
            Logout
          </Button>
        </MotionBox>
      )}
    </AnimatePresence>,
    document.body
  );

  return (
    <>
      <Button
        ref={buttonRef}
        as="button"
        rounded="full"
        variant="link"
        cursor="pointer"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Avatar size="sm" name={user?.name} src={user?.picture} />
      </Button>
      {menuContent}
    </>
  );
};

const WithSubnavigation: React.FC = () => {
  const { isOpen, onToggle } = useDisclosure();
  const { user, logout } = useAuth0();
  const { isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  const navigateToProfile = () => {
    navigate('/profile');
  };

  const navigateToSettings = () => {
    navigate('/settings');
  };

  const handleLogout = () => {
    clearAuthData();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <Box position="sticky" top="0" zIndex="sticky" width="100%">
      <Flex
        height="4rem"
        padding="0.5rem 2rem"
        bg="background.brandDark"
        align="center"
      >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', md: 'none' }}
        >
          <IconButton
            onClick={onToggle}
            icon={
              isOpen ? (
                <CloseIcon w={3} h={3} />
              ) : (
                <HamburgerIcon w={5} h={5} color="white" />
              )
            }
            variant="ghost"
            aria-label="Toggle Navigation"
          />
        </Flex>
        <Flex
          flex={{ base: 1 }}
          justify={{ base: 'center', md: 'space-between' }}
        >
          <Image
            src={DenimLogo}
            cursor="pointer"
            onClick={() => navigate('/')}
          />
          <Flex display={{ base: 'none', md: 'flex' }}>
            <DesktopNav />
          </Flex>
          <Stack justify="flex-end" direction="row" spacing={6}>
            {isAuthenticated ? (
              <>
                <OmniSearch />
                <ProfileMenu
                  user={user}
                  navigateToProfile={navigateToProfile}
                  navigateToSettings={navigateToSettings}
                  handleLogout={handleLogout}
                />
              </>
            ) : (
              <LoginButton />
            )}
          </Stack>
        </Flex>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <MobileNav />
      </Collapse>
    </Box>
  );
};

const DesktopNav = () => {
  const { isAuthenticated } = useAuth0();
  const { permissions, isReady } = usePermissions();
  const prefersReducedMotion = useReducedMotion();
  const hasFollowUpAccess = isReady
    ? permissions.canAccessFollowUpScheduling
    : hasFollowUpSchedulingAccessFromStorage();
  const popoverContentBgColor = 'white';
  const visibleNavItems = NAV_ITEMS.filter(
    (navItem) =>
      !navItem.requiresFollowUpSchedulingAccess || hasFollowUpAccess
  );

  return (
    <Stack direction="row" spacing={4} alignContent="center">
      {isAuthenticated && (
        <>
          {visibleNavItems.map((navItem) => (
            <MotionBox
              key={navItem.label}
              display="flex"
              alignItems="center"
              gap="1rem"
              whileHover={prefersReducedMotion ? undefined : { y: -1 }}
            >
              <Popover trigger="hover" placement="bottom-start">
                <PopoverTrigger>
                  {navItem.external ? (
                    <a
                      href={navItem.href ?? '#'}
                      className="nav-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {navItem.label}
                    </a>
                  ) : (
                    <NavLink
                      to={navItem.href ?? '#'}
                      className={({ isActive }) =>
                        isActive
                          ? 'active-navlink nav-link'
                          : 'nav-link'
                      }
                    >
                      {navItem.label}
                    </NavLink>
                  )}
                </PopoverTrigger>

                {navItem.children && (
                  <PopoverContent
                    border={0}
                    boxShadow="xl"
                    bg={popoverContentBgColor}
                    p={4}
                    rounded="xl"
                    minW="sm"
                  >
                    <Stack>
                      {navItem.children.map((child) => (
                        <DesktopSubNav key={child.label} {...child} />
                      ))}
                    </Stack>
                  </PopoverContent>
                )}
              </Popover>
            </MotionBox>
          ))}
        </>
      )}
    </Stack>
  );
};

const DesktopSubNav = ({ label, href, subLabel }: NavItem) => {
  const navigate = useNavigate();

  return (
    <Box
      onClick={() => navigate(href ?? '#')}
      role="group"
      display="block"
      p={2}
      rounded="md"
      _hover={{ bg: useColorModeValue('blue.50', 'gray.900') }}
      cursor="pointer"
    >
      <Stack direction="row" align="center">
        <Box>
          <Text
            transition="all .3s ease"
            _groupHover={{ color: 'blue.400' }}
            fontWeight={500}
          >
            {label}
          </Text>
          <Text fontSize="sm">{subLabel}</Text>
        </Box>
        <Flex
          transition="all .3s ease"
          transform="translateX(-10px)"
          opacity={0}
          _groupHover={{
            opacity: '100%',
            transform: 'translateX(0)',
          }}
          justify="flex-end"
          align="center"
          flex={1}
        >
          <Icon color="blue.400" w={5} h={5} as={ChevronRightIcon} />
        </Flex>
      </Stack>
    </Box>
  );
};

const MobileNav = () => {
  const { isAuthenticated } = useAuth0();
  const { permissions, isReady } = usePermissions();
  const hasFollowUpAccess = isReady
    ? permissions.canAccessFollowUpScheduling
    : hasFollowUpSchedulingAccessFromStorage();
  const visibleNavItems = NAV_ITEMS.filter(
    (navItem) =>
      !navItem.requiresFollowUpSchedulingAccess || hasFollowUpAccess
  );

  return (
    <Stack
      bg={useColorModeValue('white', 'gray.800')}
      p={4}
      display={{ md: 'none' }}
    >
      {isAuthenticated &&
        visibleNavItems.map((navItem) => (
          <MobileNavItem key={navItem.label} {...navItem} />
        ))}
    </Stack>
  );
};

const MobileNavItem = ({ label, children, href }: NavItem) => {
  const { isOpen, onToggle } = useDisclosure();
  const prefersReducedMotion = useReducedMotion();

  return (
    <MotionBox
      display="flex"
      flexDirection="column"
      sx={{ gap: '1rem' }}
      onClick={children ? onToggle : undefined}
      initial={
        prefersReducedMotion
          ? { opacity: 1, y: 0 }
          : { opacity: 0, y: 4 }
      }
      animate={{ opacity: 1, y: 0 }}
    >
      <Box
        py={2}
        as="a"
        href={href ?? '#'}
        justifyContent="space-between"
        alignItems="center"
        _hover={{
          textDecoration: 'none',
        }}
      >
        <Text
          fontWeight={600}
          color={useColorModeValue('gray.600', 'gray.200')}
        >
          {label}
        </Text>
        {children && (
          <Icon
            as={ChevronDownIcon}
            transition="all .25s ease-in-out"
            transform={isOpen ? 'rotate(180deg)' : ''}
            w={6}
            h={6}
          />
        )}
      </Box>

      <Collapse
        in={isOpen}
        animateOpacity
        style={{ marginTop: '0!important' }}
      >
        <Stack
          mt={2}
          pl={4}
          borderLeft={1}
          borderStyle="solid"
          borderColor={useColorModeValue('gray.200', 'gray.700')}
          align="start"
        >
          {children &&
            children.map((child) => (
              <Box as="a" key={child.label} py={2} href={child.href}>
                {child.label}
              </Box>
            ))}
        </Stack>
      </Collapse>
    </MotionBox>
  );
};

interface NavItem {
  label: string;
  subLabel?: string;
  children?: Array<NavItem>;
  href?: string;
  external?: boolean;
  requiresFollowUpSchedulingAccess?: boolean;
}

const NAV_ITEMS: Array<NavItem> = [
  {
    label: 'Practices',
    href: 'practices',
  },
  {
    label: 'Providers',
    href: 'providers',
  },
  {
    label: 'Plans',
    href: 'plans',
  },
  {
    label: 'Follow-Up Scheduling',
    href: '/follow-up-scheduling',
    requiresFollowUpSchedulingAccess: true,
  },
];

export default WithSubnavigation;
